import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  limit,
  serverTimestamp,
  runTransaction,
} from 'firebase/firestore';
import { db } from '@/shared/config/firebase';
import {
  Customer,
  CustomerSchema,
  CreateCustomerRequest
} from '@/models/customer';

const EMAIL_INDEX_COLLECTION = 'customerIndexByEmail';
const UID_INDEX_COLLECTION = 'customerIndexByUid';

/**
 * Repository pour gérer les customers
 * Gère les clients pour les commandes et invitations
 */
export class CustomerRepository {
  private static readonly COLLECTION_NAME = 'customers';

  static normalizeEmail(email: string): string {
    return email.trim().toLowerCase();
  }

  private static parseCustomerDoc(id: string, data: Record<string, unknown>): Customer {
    return CustomerSchema.parse({ id, ...data });
  }

  private static emailIndexRef(normalizedEmail: string) {
    return doc(db, EMAIL_INDEX_COLLECTION, normalizedEmail);
  }

  private static uidIndexRef(firebaseUid: string) {
    return doc(db, UID_INDEX_COLLECTION, firebaseUid);
  }

  /**
   * Rétrocompatibilité : retrouve un customer existant sans index (données legacy)
   */
  private static async findLegacyCustomer(
    normalizedEmail: string,
    firebaseUid?: string
  ): Promise<Customer | null> {
    if (firebaseUid) {
      const byUid = await this.findByFirebaseUid(firebaseUid);
      if (byUid) return byUid;
    }
    return this.findByEmail(normalizedEmail);
  }

  /**
   * Crée les index email/uid pour un customer existant (migration lazy)
   */
  private static async ensureIndexes(customer: Customer): Promise<void> {
    const normalizedEmail = this.normalizeEmail(customer.email);
    const batch: Promise<void>[] = [];

    const emailIndexSnap = await getDoc(this.emailIndexRef(normalizedEmail));
    if (!emailIndexSnap.exists()) {
      batch.push(
        runTransaction(db, async (transaction) => {
          const ref = this.emailIndexRef(normalizedEmail);
          const snap = await transaction.get(ref);
          if (!snap.exists()) {
            transaction.set(ref, { customerId: customer.id, createdAt: serverTimestamp() });
          }
        }).then(() => undefined)
      );
    }

    if (customer.firebaseUid) {
      const uidIndexSnap = await getDoc(this.uidIndexRef(customer.firebaseUid));
      if (!uidIndexSnap.exists()) {
        batch.push(
          runTransaction(db, async (transaction) => {
            const ref = this.uidIndexRef(customer.firebaseUid!);
            const snap = await transaction.get(ref);
            if (!snap.exists()) {
              transaction.set(ref, { customerId: customer.id, createdAt: serverTimestamp() });
            }
          }).then(() => undefined)
        );
      }
    }

    await Promise.all(batch);
  }

  /**
   * Détecte tous les customers partageant le même firebaseUid (legacy + index)
   */
  static async findAllByFirebaseUid(firebaseUid: string): Promise<Customer[]> {
    const results: Customer[] = [];
    const seenIds = new Set<string>();

    const indexSnap = await getDoc(this.uidIndexRef(firebaseUid));
    if (indexSnap.exists()) {
      const customerId = indexSnap.data()?.customerId as string;
      const customerSnap = await getDoc(doc(db, this.COLLECTION_NAME, customerId));
      if (customerSnap.exists()) {
        results.push(this.parseCustomerDoc(customerSnap.id, customerSnap.data() || {}));
        seenIds.add(customerSnap.id);
      }
    }

    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('firebaseUid', '==', firebaseUid)
    );
    const querySnapshot = await getDocs(q);

    for (const customerDoc of querySnapshot.docs) {
      if (!seenIds.has(customerDoc.id)) {
        results.push(this.parseCustomerDoc(customerDoc.id, customerDoc.data()));
      }
    }

    if (results.length > 1) {
      console.error(
        `🚨 DOUBLON firebaseUid détecté: ${firebaseUid} → ${results.map((c) => c.id).join(', ')}`
      );
    }

    return results;
  }

  /**
   * Garde-fou : refuse si un autre customer utilise déjà ce firebaseUid
   */
  static async assertUniqueFirebaseUid(
    firebaseUid: string,
    excludeCustomerId?: string
  ): Promise<void> {
    const duplicates = await this.findAllByFirebaseUid(firebaseUid);
    const conflict = duplicates.find((c) => c.id !== excludeCustomerId);
    if (conflict) {
      throw new Error(
        `Le Firebase UID ${firebaseUid} est déjà associé au customer ${conflict.id}`
      );
    }
  }

  static async findByEmail(email: string): Promise<Customer | null> {
    try {
      const normalizedEmail = this.normalizeEmail(email);
      console.log(`🔍 Recherche customer par email: ${normalizedEmail}`);

      const indexSnap = await getDoc(this.emailIndexRef(normalizedEmail));
      if (indexSnap.exists()) {
        const customerId = indexSnap.data()?.customerId as string;
        const customerSnap = await getDoc(doc(db, this.COLLECTION_NAME, customerId));
        if (customerSnap.exists()) {
          return this.parseCustomerDoc(customerSnap.id, customerSnap.data() || {});
        }
      }

      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('email', '==', normalizedEmail),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        console.log(`❌ Aucun customer trouvé pour l'email: ${normalizedEmail}`);
        return null;
      }

      const customerDoc = querySnapshot.docs[0];
      const customer = this.parseCustomerDoc(customerDoc.id, customerDoc.data());
      await this.ensureIndexes(customer);
      console.log(`✅ Customer trouvé: ${customer.id}`);
      return customer;

    } catch (error) {
      console.error(`❌ Erreur lors de la recherche du customer par email ${email}:`, error);
      throw error;
    }
  }

  /**
   * Récupère un customer par Firebase UID
   */
  static async findByFirebaseUid(firebaseUid: string): Promise<Customer | null> {
    try {
      console.log(`🔍 Recherche customer par Firebase UID: ${firebaseUid}`);

      const indexSnap = await getDoc(this.uidIndexRef(firebaseUid));
      if (indexSnap.exists()) {
        const customerId = indexSnap.data()?.customerId as string;
        const customerSnap = await getDoc(doc(db, this.COLLECTION_NAME, customerId));
        if (customerSnap.exists()) {
          return this.parseCustomerDoc(customerSnap.id, customerSnap.data() || {});
        }
      }

      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('firebaseUid', '==', firebaseUid),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        console.log(`❌ Aucun customer trouvé pour le Firebase UID: ${firebaseUid}`);
        return null;
      }

      const customerDoc = querySnapshot.docs[0];
      const customer = this.parseCustomerDoc(customerDoc.id, customerDoc.data());
      await this.ensureIndexes(customer);
      console.log(`✅ Customer trouvé: ${customer.id}`);
      return customer;

    } catch (error) {
      console.error(`❌ Erreur lors de la recherche du customer par Firebase UID ${firebaseUid}:`, error);
      throw error;
    }
  }

  /**
   * Crée un nouveau customer (garde-fous transactionnels via index)
   */
  static async create(request: CreateCustomerRequest): Promise<Customer> {
    const normalizedEmail = this.normalizeEmail(request.email);
    return this.findOrCreateByEmail(normalizedEmail, request.firebaseUid, request.providers || []);
  }

  /**
   * Met à jour un customer
   */
  static async update(customerId: string, request: any): Promise<Customer> {
    try {
      console.log(`🔍 Mise à jour du customer: ${customerId}`);

      if (request.firebaseUid) {
        await this.assertUniqueFirebaseUid(request.firebaseUid, customerId);
      }

      const customerRef = doc(db, this.COLLECTION_NAME, customerId);
      const updateData: Record<string, unknown> = {
        updatedAt: serverTimestamp(),
      };

      if (request.firebaseUid !== undefined && request.firebaseUid !== null) {
        updateData.firebaseUid = request.firebaseUid;
      }
      if (request.providers !== undefined && request.providers !== null) {
        updateData.providers = request.providers;
      }
      if (request.firstName !== undefined && request.firstName !== null) {
        updateData.firstName = request.firstName;
      }
      if (request.lastName !== undefined && request.lastName !== null) {
        updateData.lastName = request.lastName;
      }
      if (request.tiktokUsername !== undefined && request.tiktokUsername !== null) {
        updateData.tiktokUsername = request.tiktokUsername;
      }
      if (request.phone !== undefined && request.phone !== null) {
        updateData.phone = request.phone;
      }
      if (request.address !== undefined && request.address !== null) {
        updateData.address = request.address;
      }
      if (request.houseNumber !== undefined && request.houseNumber !== null) {
        updateData.houseNumber = request.houseNumber;
      }
      if (request.city !== undefined && request.city !== null) {
        updateData.city = request.city;
      }
      if (request.postalCode !== undefined && request.postalCode !== null) {
        updateData.postalCode = request.postalCode;
      }
      if (request.country !== undefined && request.country !== null) {
        updateData.country = request.country;
      }
      if (request.theme !== undefined && request.theme !== null) {
        updateData.theme = request.theme;
      }

      await updateDoc(customerRef, updateData);

      const updatedDoc = await getDoc(customerRef);
      const customer = this.parseCustomerDoc(updatedDoc.id, updatedDoc.data() || {});

      if (request.firebaseUid && customer.firebaseUid) {
        await runTransaction(db, async (transaction) => {
          const uidIndexRef = this.uidIndexRef(customer.firebaseUid!);
          const uidIndexSnap = await transaction.get(uidIndexRef);
          if (uidIndexSnap.exists()) {
            const indexedId = uidIndexSnap.data()?.customerId as string;
            if (indexedId !== customerId) {
              throw new Error(
                `Firebase UID ${customer.firebaseUid} déjà indexé pour le customer ${indexedId}`
              );
            }
          } else {
            transaction.set(uidIndexRef, {
              customerId,
              createdAt: serverTimestamp(),
            });
          }
        });
      }

      console.log(`✅ Customer mis à jour avec succès: ${customer.id}`);
      return customer;

    } catch (error) {
      console.error(`❌ Erreur lors de la mise à jour du customer ${customerId}:`, error);
      throw error;
    }
  }

  /**
   * Trouve ou crée un customer par email (transaction atomique anti-doublon)
   */
  static async findOrCreateByEmail(
    email: string,
    firebaseUid?: string,
    providers: CreateCustomerRequest['providers'] = []
  ): Promise<Customer> {
    const normalizedEmail = this.normalizeEmail(email);

    if (firebaseUid) {
      const existingByUid = await this.findByFirebaseUid(firebaseUid);
      if (existingByUid) {
        console.log(`✅ Customer existant trouvé par UID: ${existingByUid.id}`);
        return existingByUid;
      }
    }

    try {
      const customerId = await runTransaction(db, async (transaction) => {
        const emailIndexRef = this.emailIndexRef(normalizedEmail);
        const emailIndexSnap = await transaction.get(emailIndexRef);

        let uidIndexSnap = null;
        const uidIndexRef = firebaseUid ? this.uidIndexRef(firebaseUid) : null;
        if (uidIndexRef) {
          uidIndexSnap = await transaction.get(uidIndexRef);
        }

        const emailCustomerId = emailIndexSnap.exists()
          ? (emailIndexSnap.data()?.customerId as string)
          : null;
        const uidCustomerId = uidIndexSnap?.exists()
          ? (uidIndexSnap.data()?.customerId as string)
          : null;

        if (emailCustomerId && uidCustomerId && emailCustomerId !== uidCustomerId) {
          console.warn(
            `⚠️ Conflit index customer (email=${emailCustomerId}, uid=${uidCustomerId}). ` +
            `Conservation du customer lié au Firebase UID.`
          );
          transaction.set(emailIndexRef, {
            customerId: uidCustomerId,
            updatedAt: serverTimestamp(),
          });
        }

        const existingCustomerId = uidCustomerId || emailCustomerId;
        if (existingCustomerId) {
          const customerRef = doc(db, this.COLLECTION_NAME, existingCustomerId);
          const customerSnap = await transaction.get(customerRef);

          if (customerSnap.exists()) {
            const data = customerSnap.data() || {};
            if (firebaseUid && data.firebaseUid && data.firebaseUid !== firebaseUid) {
              throw new Error(
                `Le customer ${existingCustomerId} a déjà un firebaseUid différent: ${data.firebaseUid}`
              );
            }
            if (firebaseUid && !data.firebaseUid) {
              transaction.update(customerRef, {
                firebaseUid,
                updatedAt: serverTimestamp(),
              });
              if (uidIndexRef && !uidIndexSnap?.exists()) {
                transaction.set(uidIndexRef, {
                  customerId: existingCustomerId,
                  createdAt: serverTimestamp(),
                });
              }
            }
            return existingCustomerId;
          }
        }

        if (firebaseUid && uidIndexSnap?.exists()) {
          throw new Error(
            `Firebase UID ${firebaseUid} déjà indexé — création refusée`
          );
        }

        const newCustomerRef = doc(collection(db, this.COLLECTION_NAME));
        const customerData: Record<string, unknown> = {
          email: normalizedEmail,
          providers,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        if (firebaseUid) {
          customerData.firebaseUid = firebaseUid;
        }

        transaction.set(newCustomerRef, customerData);
        transaction.set(emailIndexRef, {
          customerId: newCustomerRef.id,
          createdAt: serverTimestamp(),
        });
        if (uidIndexRef && firebaseUid) {
          transaction.set(uidIndexRef, {
            customerId: newCustomerRef.id,
            createdAt: serverTimestamp(),
          });
        }

        return newCustomerRef.id;
      });

      const customerSnap = await getDoc(doc(db, this.COLLECTION_NAME, customerId));
      const customer = this.parseCustomerDoc(customerId, customerSnap.data() || {});
      console.log(`✅ Customer trouvé/créé: ${customer.id}`);
      return customer;

    } catch (error) {
      console.error(`❌ Erreur lors de findOrCreateByEmail pour ${normalizedEmail}:`, error);

      // Fallback : un autre onglet/processus a peut-être créé le customer entre-temps
      const legacy = await this.findLegacyCustomer(normalizedEmail, firebaseUid);
      if (legacy) {
        await this.ensureIndexes(legacy);
        return legacy;
      }
      throw error;
    }
  }

  /**
   * Met à jour le thème préféré d'un customer
   */
  static async updateTheme(customerId: string, theme: string): Promise<void> {
    try {
      console.log(`🎨 Mise à jour du thème pour customer ${customerId}: ${theme}`);

      const customerRef = doc(db, this.COLLECTION_NAME, customerId);
      await updateDoc(customerRef, {
        theme,
        updatedAt: serverTimestamp(),
      });

      console.log(`✅ Thème mis à jour avec succès pour customer ${customerId}`);
    } catch (error) {
      console.error(`❌ Erreur lors de la mise à jour du thème pour customer ${customerId}:`, error);
      throw error;
    }
  }
}
